import { ArrowLeft, Plus, RefreshCw } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useToast } from '@/components/shared/Toast';
import { apiErrorMessage } from '@/lib/apiError';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  type DirectoryStore,
  useCreateStoreMutation,
  useGetStoreQuery,
  useGetStoresQuery,
  useGetWorkspacesQuery,
  useCreateWorkspaceMutation,
  useUpdateStoreMutation,
} from '@/store/api/directoryApi';

export function StoresPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const isCreate = storeId === 'new';
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [rows, setRows] = useState<DirectoryStore[]>([]);
  const {
    data: page,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetStoresQuery(
    {
      cursor,
      limit: 25,
      search: search.trim() || undefined,
      status: status.trim() || undefined,
    },
    { skip: Boolean(storeId) },
  );
  const {
    data: store,
    isLoading: isLoadingStore,
    isError: isStoreError,
    refetch: refetchStore,
  } = useGetStoreQuery(storeId ?? '', { skip: !storeId || isCreate });
  const [createStore, { isLoading: isCreating }] = useCreateStoreMutation();
  const [updateStore, { isLoading: isSaving }] = useUpdateStoreMutation();
  const [form, setForm] = useState({ name: '', timezone: '', currency: '', status: '' });
  const [edit, setEdit] = useState(false);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);

  useEffect(() => {
    setRows([]);
    setCursor(undefined);
  }, [search, status]);

  useEffect(() => {
    if (!page) {
      return;
    }
    setRows((previous) => {
      if (!cursor) {
        return page.data;
      }
      const seen = new Set(previous.map((row) => row.id));
      return [...previous, ...page.data.filter((row) => !seen.has(row.id))];
    });
  }, [page, cursor]);

  useEffect(() => {
    if (store) {
      setForm({
        name: store.name,
        timezone: store.timezone,
        currency: store.currency,
        status: store.status,
      });
    }
  }, [store]);

  async function persist() {
    try {
      if (isCreate) {
        const created = await createStore({
          name: form.name.trim(),
          timezone: form.timezone.trim(),
          currency: form.currency.trim().toUpperCase(),
        }).unwrap();
        toast.success(`Store “${created.name}” created`);
        navigate(`/stores/${created.id}`);
      } else if (storeId) {
        const updated = await updateStore({
          id: storeId,
          body: {
            name: form.name.trim(),
            timezone: form.timezone.trim(),
            status: form.status.trim() || undefined,
          },
        }).unwrap();
        toast.success(`Store “${updated.name}” saved`);
        setEdit(false);
        setStatusConfirmOpen(false);
      }
    } catch (error: unknown) {
      toast.error(
        apiErrorMessage(
          error,
          isCreate
            ? 'Could not create the store. Check the fields and try again.'
            : 'Could not save the store. Your changes are still here; try again.',
        ),
      );
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.name.trim() || !form.timezone.trim() || !form.currency.trim()) {
      return;
    }
    if (!isCreate && store && form.status.trim() && form.status.trim() !== store.status) {
      setStatusConfirmOpen(true);
      return;
    }
    await persist();
  }

  if (isCreate || storeId) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/stores')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Stores &amp; workspaces
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            {isCreate ? 'Create store' : (store?.name ?? 'Store settings')}
          </h1>
          <p className="text-sm text-muted-foreground">
            Store scope and settings. Organization policies determine which actions are available.
          </p>
        </div>
        {isCreate || edit ? (
          <Card>
            <CardContent className="pt-6">
              <form
                className="max-w-xl space-y-4"
                onSubmit={(event) => {
                  void submit(event);
                }}
              >
                <div className="space-y-2">
                  <Label htmlFor="store-name">Store name</Label>
                  <Input
                    id="store-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="store-timezone">Timezone</Label>
                  <Input
                    id="store-timezone"
                    required
                    title="Enter an IANA timezone, such as Europe/Madrid."
                    placeholder="e.g. Europe/Madrid"
                    value={form.timezone}
                    onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">Use an IANA timezone name.</p>
                </div>
                {isCreate ? (
                  <div className="space-y-2">
                    <Label htmlFor="store-currency">Currency</Label>
                    <Input
                      id="store-currency"
                      required
                      maxLength={3}
                      pattern="[A-Za-z]{3}"
                      placeholder="e.g. EUR"
                      value={form.currency}
                      onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Three-letter currency code. The organization currency is fixed after its first
                      store.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Currency: {store?.currency ?? '—'} (not editable)
                  </p>
                )}
                {!isCreate && (
                  <div className="space-y-2">
                    <Label htmlFor="store-status-edit">Status</Label>
                    <Input
                      id="store-status-edit"
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Changing status is confirmed before save.
                    </p>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => (isCreate ? navigate('/stores') : setEdit(false))}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isCreating || isSaving}>
                    {isCreating || isSaving ? 'Saving…' : 'Save store'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ) : isLoadingStore ? (
          <LoadingSpinner className="py-10" />
        ) : isStoreError || !store ? (
          <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">
            Could not load this store.{' '}
            <Button
              variant="link"
              onClick={() => {
                void refetchStore();
              }}
            >
              Try again
            </Button>
          </div>
        ) : (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <dl className="grid gap-4 sm:grid-cols-2">
                <Detail label="Store ID" value={store.id} mono />
                <Detail label="Status" value={store.status} />
                <Detail label="Timezone" value={store.timezone} />
                <Detail label="Currency" value={store.currency} />
                <Detail label="Created" value={new Date(store.createdAt).toLocaleString()} />
              </dl>
              <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
                <Button variant="outline" onClick={() => setEdit(true)}>
                  Edit settings
                </Button>
                <Button asChild>
                  <Link to={`/stores/${store.id}/roles`}>Simple roles</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        <ConfirmDialog
          open={statusConfirmOpen}
          onOpenChange={setStatusConfirmOpen}
          title="Change store status"
          description={`Change the status of “${store?.name ?? ''}” from “${store?.status ?? ''}” to “${form.status.trim()}”?`}
          confirmLabel="Change status"
          onConfirm={() => {
            void persist();
          }}
          isLoading={isSaving}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Stores &amp; workspaces</h1>
          <p className="text-sm text-muted-foreground">
            Manage organization-scoped commerce locations and their work areas.
          </p>
        </div>
        <Button asChild>
          <Link to="/stores/new">
            <Plus className="mr-2 h-4 w-4" /> Create store
          </Link>
        </Button>
      </div>
      <div className="flex flex-wrap gap-3">
        <div className="min-w-56 flex-1">
          <Label htmlFor="store-search">Search stores</Label>
          <Input
            id="store-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Store name"
          />
        </div>
        <div className="w-48">
          <Label htmlFor="store-status">Status</Label>
          <Input
            id="store-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            placeholder="Any status"
          />
        </div>
      </div>
      <section aria-labelledby="stores-heading" aria-busy={isLoading || isFetching}>
        <h2 id="stores-heading" className="mb-3 text-lg font-semibold">
          Stores
        </h2>
        {isLoading && rows.length === 0 ? (
          <LoadingRows label="Loading stores" columns={6} />
        ) : isError && rows.length === 0 ? (
          <Retry
            onRetry={() => {
              void refetch();
            }}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title={search || status ? 'No matching stores' : 'No stores yet'}
            description="Stores define the organization scope for commerce workspaces."
            action={
              search || status ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearch('');
                    setStatus('');
                  }}
                >
                  Clear filters
                </Button>
              ) : (
                <Button asChild>
                  <Link to="/stores/new">Create store</Link>
                </Button>
              )
            }
          />
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto" role="region" aria-label="Stores table">
                <table className="w-full text-left text-sm">
                  <thead className="border-b bg-muted/40">
                    <tr>
                      {['Name', 'Identifier', 'Status', 'Timezone', 'Currency', 'Created'].map(
                        (label) => (
                          <th key={label} className="px-4 py-3 font-medium">
                            {label}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} className="border-b last:border-0 hover:bg-muted/20">
                        <td className="px-4 py-3 font-medium">
                          <Link className="text-primary hover:underline" to={`/stores/${row.id}`}>
                            {row.name}
                          </Link>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{row.id}</td>
                        <td className="px-4 py-3">{row.status}</td>
                        <td className="px-4 py-3">{row.timezone}</td>
                        <td className="px-4 py-3">{row.currency}</td>
                        <td className="px-4 py-3">
                          {new Date(row.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
        {isError && rows.length > 0 && (
          <Retry
            onRetry={() => {
              void refetch();
            }}
          />
        )}
        {page?.pagination.hasMore && (
          <div className="mt-3 flex justify-center">
            <Button
              variant="outline"
              disabled={isFetching}
              onClick={() => setCursor(page.pagination.cursor ?? undefined)}
            >
              {isFetching ? 'Loading…' : 'Load more stores'}
            </Button>
          </div>
        )}
      </section>
      <WorkspacesSection />
    </div>
  );
}

function WorkspacesSection() {
  const toast = useToast();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [storeId, setStoreId] = useState('');
  const [status, setStatus] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [rows, setRows] = useState<import('@/store/api/directoryApi').DirectoryWorkspace[]>([]);
  const { data: stores } = useGetStoresQuery({ limit: 100 });
  const {
    data: page,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetWorkspacesQuery({
    cursor,
    limit: 25,
    search: search.trim() || undefined,
    status: status.trim() || undefined,
    storeId: storeId || undefined,
  });
  const [createWorkspace, { isLoading: isCreating }] = useCreateWorkspaceMutation();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ storeId: '', name: '', kind: '' });

  useEffect(() => {
    setRows([]);
    setCursor(undefined);
  }, [search, status, storeId]);
  useEffect(() => {
    if (!page) {
      return;
    }
    setRows((previous) => {
      if (!cursor) {
        return page.data;
      }
      const seen = new Set(previous.map((row) => row.id));
      return [...previous, ...page.data.filter((row) => !seen.has(row.id))];
    });
  }, [page, cursor]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.storeId || !form.name.trim() || !form.kind.trim()) {
      return;
    }
    try {
      const created = await createWorkspace({
        storeId: form.storeId,
        name: form.name.trim(),
        kind: form.kind.trim(),
      }).unwrap();
      toast.success(`Workspace “${created.name}” created`);
      setCreateOpen(false);
      setForm({ storeId: '', name: '', kind: '' });
      navigate(`/workspaces/${created.id}`);
    } catch (error: unknown) {
      toast.error(
        apiErrorMessage(error, 'Could not create the workspace. Check the fields and try again.'),
      );
    }
  }

  return (
    <section aria-labelledby="workspaces-heading" aria-busy={isLoading || isFetching}>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="workspaces-heading" className="text-lg font-semibold">
            Workspaces
          </h2>
          <p className="text-sm text-muted-foreground">Workspaces are associated with a store.</p>
        </div>
        <Button
          onClick={() => {
            setCreateOpen(true);
            setForm((current) => ({ ...current, storeId: storeId || '' }));
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> Create workspace
        </Button>
      </div>
      <div className="mb-3 grid gap-3 sm:grid-cols-3">
        <div>
          <Label htmlFor="workspace-search">Search workspaces</Label>
          <Input
            id="workspace-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Workspace name"
          />
        </div>
        <div>
          <Label htmlFor="workspace-store-filter">Store</Label>
          <select
            id="workspace-store-filter"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
          >
            <option value="">All stores</option>
            {(stores?.data ?? []).map((store) => (
              <option key={store.id} value={store.id}>
                {store.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="workspace-status-filter">Status</Label>
          <Input
            id="workspace-status-filter"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            placeholder="Any status"
          />
        </div>
      </div>
      {isLoading && rows.length === 0 ? (
        <LoadingRows label="Loading workspaces" columns={5} />
      ) : isError && rows.length === 0 ? (
        <Retry
          onRetry={() => {
            void refetch();
          }}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={search || storeId || status ? 'No matching workspaces' : 'No workspaces yet'}
          description="Create a workspace and associate it with a store."
          action={
            search || storeId || status ? (
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setStoreId('');
                  setStatus('');
                }}
              >
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto" role="region" aria-label="Workspaces table">
              <table className="w-full text-left text-sm">
                <thead className="border-b bg-muted/40">
                  <tr>
                    {['Name', 'Identifier', 'Store', 'Kind', 'Status'].map((label) => (
                      <th key={label} className="px-4 py-3 font-medium">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b last:border-0 hover:bg-muted/20">
                      <td className="px-4 py-3 font-medium">
                        <Link className="text-primary hover:underline" to={`/workspaces/${row.id}`}>
                          {row.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{row.id}</td>
                      <td className="px-4 py-3">
                        {(stores?.data ?? []).find((store) => store.id === row.storeId)?.name ??
                          row.storeId}
                      </td>
                      <td className="px-4 py-3">{row.kind}</td>
                      <td className="px-4 py-3">{row.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      {isError && rows.length > 0 && (
        <Retry
          onRetry={() => {
            void refetch();
          }}
        />
      )}
      {page?.pagination.hasMore && (
        <div className="mt-3 flex justify-center">
          <Button
            variant="outline"
            disabled={isFetching}
            onClick={() => setCursor(page.pagination.cursor ?? undefined)}
          >
            {isFetching ? 'Loading…' : 'Load more workspaces'}
          </Button>
        </div>
      )}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create workspace</DialogTitle>
            <DialogDescription>Choose a store and provide the workspace details.</DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              void submit(event);
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="workspace-create-store">Store</Label>
              <select
                id="workspace-create-store"
                required
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.storeId}
                onChange={(e) => setForm({ ...form, storeId: e.target.value })}
              >
                <option value="">Select a store</option>
                {(stores?.data ?? []).map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="workspace-create-name">Workspace name</Label>
              <Input
                id="workspace-create-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="workspace-create-kind">Kind</Label>
              <Input
                id="workspace-create-kind"
                required
                placeholder="e.g. retail"
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button disabled={isCreating} type="submit">
                {isCreating ? 'Saving…' : 'Create workspace'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Detail({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className={`mt-1 text-sm ${mono ? 'break-all font-mono' : ''}`}>{value || '—'}</dd>
    </div>
  );
}

function Retry({ onRetry }: { onRetry: () => void }) {
  return (
    <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">
      Could not load this list.{' '}
      <Button variant="link" onClick={onRetry}>
        <RefreshCw className="mr-1 h-3 w-3" /> Try again
      </Button>
    </div>
  );
}
